import { useApiGen } from "$root/api/hooks";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LeavePrompt } from "$root/components/LeavePrompt";
import { PageHeader } from "$root/components/PageHeader";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import {
	EditPortfolioMultiV4ContextProvider,
	EditPortfolioMultiV4InContext,
} from "$root/components/Portfolio/CreatePortfolio/EditPortfolioMulti";
import { makeUnsavedDataHandler } from "$root/components/Portfolio/CreatePortfolio/shared";
import {
	type EnhancementAxiosError,
	spawnUniverseConflictStrategyDialog,
	universeConflictStrategiesPayload,
} from "$root/components/Portfolio/CreatePortfolio/spawn-universe-conflict-strategy";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { platformToast } from "$root/notification-system/toast";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useQueryNoRefetch } from "$root/third-party-integrations/react-query/hooks";
import {
	InvestmentBulkEnhancementConfigurationControllerV4ApiFactory,
	type UniverseConflictResolution,
} from "@eu.mdotm.api/sphere";
import type { AxiosError } from "axios";
import { Button } from "@mdotm/shadcn/mdotm/components";
import { ActionText, AutoTooltip, Icon, Text, TooltipContent } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { noop } from "@mdotm/shadcn/legacy/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePortfolioStudioRedirect } from "../PortfoliosStudio/usePortfolioStudioRedirect";

/** New/Edit merged together */
export default function CreatePortfolioMulti({
	workspaceUid,
	proposalUuid,
	intentId,
}: {
	workspaceUid?: string;
	proposalUuid: string;
	intentId?: string;
}): React.JSX.Element {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { t } = useTranslation();

	const bulkEnhancementApi = useApiGen(InvestmentBulkEnhancementConfigurationControllerV4ApiFactory);

	const portfoliosQuery = useQueryNoRefetch({
		queryKey: ["portfolioNames", proposalUuid],
		queryFn: () => axiosExtract(bulkEnhancementApi.getEnhancementConfigurationStepsAvailability1(proposalUuid)),
	});

	async function Submit(uuid: string, universeConflictStrategies?: UniverseConflictResolution[]) {
		try {
			setIsSubmitting(true);
			await bulkEnhancementApi.runEnhancementConfiguration1(uuid, universeConflictStrategies ?? []);
			trackMixPanelEvent("portfolio.bulkActions.createProposal", {
				intentId,
				selectedPortfolios: portfoliosQuery.data?.investments?.length ?? 0,
			});
			setIsSubmitting(false);
			portfolioRepository.invalidateEntity(uuid);
			platformToast({
				children: "Sphere has taken over your request",
				severity: "success",
				icon: "Portfolio",
			});

			portfolioStudioRedirect.navigate();
		} catch (error) {
			const axiosError = error as AxiosError<EnhancementAxiosError> | undefined;

			if (axiosError?.response?.data.code === 61033) {
				platformToast({
					children: "Unable to proceed while the universe is in review status",
					severity: "info",
					icon: "Portfolio",
				});
				return;
			}

			if (axiosError?.response?.data.code === 61043) {
				platformToast({
					children: axiosError?.response?.data.message,
					severity: "info",
					icon: "Portfolio",
				});

				const conflicts = axiosError?.response?.data.conflicts;
				spawnUniverseConflictStrategyDialog({
					flow: "bulk",
					message: axiosError?.response?.data.message ?? "Something went wrong",
					conflicts,
					onSubmit: (choices) => Submit(uuid, universeConflictStrategiesPayload(conflicts, choices)),
				});
				return;
			}

			platformToast({
				children: t("PORTFOLIOS.PORTFOLIO_GENERIC_UPLOAD_ERROR"),
				severity: "error",
				icon: "Portfolio",
			});
			console.error(error);
		}
	}

	const crumbs = useCrumbs();
	const workspacePath = useCrumbs().useWorkspacePath(workspaceUid);
	const portfolioStudioRedirect = usePortfolioStudioRedirect(workspaceUid);

	return (
		<ReactQueryWrapperBase query={portfoliosQuery}>
			{({ investments }) => (
				<EditPortfolioMultiV4ContextProvider
					area={{ name: "create-bulk-proposal", proposalUuid, edit: true, editable: true }}
				>
					{({ stepsMetadata, currentStep, setStepsMetadata, area, submittersRef }) => (
						<>
							<PageHeader
								crumbs={[
									...crumbs.portfolioStudio("Portfolios"),
									...workspacePath,
									{
										label: "Proposal",
									},
								]}
								titleAction={
									<div className="flex items-center space-x-2">
										<Button size="small" onClick={portfolioStudioRedirect.navigate} palette="tertiary">
											Cancel
										</Button>
										<Button
											size="small"
											onClickAsync={async () => {
												try {
													const saveHandler = makeUnsavedDataHandler({
														currentStep,
														setStepsMetadata,
														submittersRef,
													});
													const shouldContinue = (await saveHandler()) === "continue";
													if (!shouldContinue) {
														throw new Error("unable to proceed, some step may require additional data or corrections");
													}
													await Submit(area.proposalUuid ?? "");
												} catch (error) {
													platformToast({
														children: `Unable to proceed, ${t(
															`PORTFOLIO_WIZARD.STEP_NAME.${currentStep}`,
														)} step requires additional data or correction`,
														severity: "info",
														icon: "Portfolio",
													});
													throw error;
												}
											}}
											palette="primary"
										>
											<Icon icon="Enhance" size="1.2em" />
											&nbsp;Generate
										</Button>
									</div>
								}
								subTitle={
									<div className="flex justify-between items-center py-2.5">
										<Text>
											{investments?.length === 2 ? (
												<>
													You are creating a proposal for:{" "}
													<ActionText
														href={typedUrlForRoute("PortfolioDetails", { portfolioUid: investments?.[0].uuid ?? "" })}
													>
														{investments[0].name ?? "-"}
													</ActionText>{" "}
													and{" "}
													<ActionText
														href={typedUrlForRoute("PortfolioDetails", { portfolioUid: investments?.[1].uuid ?? "" })}
													>
														{investments[1].name ?? "-"}
													</ActionText>
												</>
											) : (
												<>
													You are creating a proposal for:{" "}
													<ActionText
														href={typedUrlForRoute("PortfolioDetails", { portfolioUid: investments?.[0].uuid ?? "" })}
													>
														{investments?.[0].name ?? "-"}
													</ActionText>{" "}
													and{" "}
													<AutoTooltip
														overrideColor={themeCSSVars.palette_N200}
														trigger={({ innerRef }) => (
															<ActionText onClick={noop} innerRef={innerRef}>
																{(investments?.length ?? 0) - 1} more
															</ActionText>
														)}
													>
														<TooltipContent>
															<ul className="list-disc pl-6">
																{(investments?.slice(1) ?? []).map((p, i) => (
																	<li key={p.uuid ?? i}>{p.name ?? "-"}</li>
																))}
															</ul>
														</TooltipContent>
													</AutoTooltip>
												</>
											)}
										</Text>
									</div>
								}
							/>
							<EditPortfolioMultiV4InContext />

							<LeavePrompt
								when={stepsMetadata[currentStep].dirty || !isSubmitting}
								title={t("LEAVE_PAGE")}
								pathToNotBlock={[
									"/login",
									`${typedUrlForRoute("Portfolios/EditPortfolioMulti", { proposalUuid })}#${currentStep}`,
									typedUrlForRoute("Portfolios/EditPortfolioMulti", { proposalUuid }),

									`${typedUrlForRoute("Portfolios/EditPortfolioMulti", { proposalUuid, intentId })}#${currentStep}`,
									typedUrlForRoute("Portfolios/EditPortfolioMulti", { proposalUuid, intentId }),

									// Reached from a workspace this is a different route sharing this component, so its own
									// URLs need whitelisting too — otherwise stepping through the rail prompts to leave the
									// page it is already on.
									...(workspaceUid
										? [
												`${typedUrlForRoute("Portfolios/EditPortfolioMultiWithWorkspace", { workspaceUid, proposalUuid })}#${currentStep}`,
												typedUrlForRoute("Portfolios/EditPortfolioMultiWithWorkspace", { workspaceUid, proposalUuid }),
												`${typedUrlForRoute("Portfolios/EditPortfolioMultiWithWorkspace", { workspaceUid, proposalUuid, intentId })}#${currentStep}`,
												typedUrlForRoute("Portfolios/EditPortfolioMultiWithWorkspace", {
													workspaceUid,
													proposalUuid,
													intentId,
												}),
											]
										: []),
								]}
							>
								{t("PORTFOLIOS.CREATE_LEAVE")}
							</LeavePrompt>
						</>
					)}
				</EditPortfolioMultiV4ContextProvider>
			)}
		</ReactQueryWrapperBase>
	);
}
